import { Link } from '../../components/SmartLink';
import {
  CheckCircle, Warning, MapPin, WhatsappLogo,
  Clock, ArrowRight, Shield, Heart,
  Users, SealCheck, CalendarBlank,
  FileText, Star, House, UsersThree,
} from '@phosphor-icons/react';
import VisaDetailLayout from '../../components/visas/VisaDetailLayout';
import { VISA_BY_ID } from '../../data/visaData';
import { buildWAUrl } from '../../lib/whatsapp';
import { ExpandableTip, AIAnswerBox } from '../../components/visas/VisaPageHelpers';

const WA_URL = buildWAUrl(
  'Hi Big Bird Travel, I am a UAE resident and want to sponsor my family (spouse/children/parents) on a residence visa. Please check my eligibility and the process.'
);

const SUMMARY_CARDS = [
  { label: 'Who You Can Sponsor', value: 'Spouse, Kids, Parents', sub: 'Subject to eligibility', Icon: UsersThree },
  { label: 'Processing Time', value: '5 – 10 Working Days', sub: 'After entry & medical test', Icon: Clock },
  { label: 'Min. Salary', value: 'From ~AED 4,000', sub: 'Or AED 3,000 + housing', Icon: FileText },
  { label: 'Golden Visa Holders', value: 'No Salary Rule', sub: 'Usually exempt', Icon: Star },
  { label: 'Apply From', value: 'Abu Dhabi / Dubai', sub: 'Sponsor-based process', Icon: MapPin },
];

const GUIDE_FOR = [
  { Icon: Heart,        label: 'UAE Residents Wanting to Sponsor a Spouse' },
  { Icon: Users,        label: 'Parents Sponsoring Children' },
  { Icon: House,        label: 'Residents Sponsoring Their Own Parents' },
  { Icon: Star,         label: 'Golden Visa Holders' },
  { Icon: FileText,     label: 'First-Time Sponsors Unsure of Salary Rules' },
  { Icon: MapPin,       label: 'Abu Dhabi, Dubai & Northern Emirates Residents' },
];

const RELATIONSHIP_TABLE = [
  { relation: 'Spouse', minSalary: 'AED 4,000 (or 3,000 + housing)', extra: 'Attested marriage certificate', badge: 'blue' },
  { relation: 'Children', minSalary: 'Same as spouse sponsorship', extra: 'Attested birth certificate(s)', badge: 'blue' },
  { relation: 'Parents', minSalary: 'Higher threshold applies', extra: 'Additional conditions apply', badge: 'navy' },
  { relation: 'Golden Visa holder sponsoring family', minSalary: 'Generally exempt', extra: 'Standard family documents', badge: 'green' },
];

const HIDDEN_FACTS = [
  {
    Icon: House,
    title: 'Your tenancy contract (Ejari) matters as much as your salary',
    desc: 'A qualifying salary is not enough on its own — you also need a valid Ejari-registered tenancy contract in your own name, suitable for the family size being sponsored. Shared or informal accommodation is usually rejected.',
  },
  {
    Icon: FileText,
    title: 'Attestation is often the slowest step, not the visa itself',
    desc: 'Marriage and birth certificates typically need attestation in the issuing country first, then by the UAE Ministry of Foreign Affairs. Starting this early avoids the biggest bottleneck in family sponsorship.',
  },
  {
    Icon: Users,
    title: 'Parent sponsorship has its own, stricter rules',
    desc: 'Sponsoring parents generally requires a higher income than sponsoring a spouse or children, plus extra conditions in some cases. We check the current rules for your specific situation before you commit.',
  },
  {
    Icon: Star,
    title: 'Golden Visa holders get real flexibility here',
    desc: 'One of the most overlooked Golden Visa benefits is sponsoring family without meeting the standard salary threshold — worth factoring in if you are also weighing a Golden Visa application.',
  },
];

const FAMILY_SERVICES = [
  { label: 'Golden Visa',           to: '/visas/golden-visa',    desc: 'No salary rule for sponsors' },
  { label: 'UAE Visit Visa',        to: '/visas/uae-visit-visa', desc: 'Short visits before sponsorship' },
  { label: 'Flights',               to: '/flights',              desc: 'Bring family to the UAE' },
  { label: 'Hotel Booking',         to: '/hotels',                desc: 'Temporary stays while settling in' },
  { label: 'All Visa Services',     to: '/visas',                 desc: 'UAE, Schengen, UK, USA & more' },
  { label: 'Contact Our Team',      to: '/contact',               desc: 'Mussafah, Abu Dhabi office' },
];

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'UAE Family & Residence Visa Sponsorship Assistance',
  description: 'UAE family visa sponsorship assistance — spouse, children and parent sponsorship for residents in Abu Dhabi and Dubai. Based in Mussafah, Abu Dhabi.',
  url: 'https://bigbirdtravel.com/visas/family-visa',
  provider: {
    '@type': 'TravelAgency',
    name: 'Big Bird Travel & Tourism',
    url: 'https://bigbirdtravel.com',
    telephone: '+971561789642',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Al Sawari Street 5, Building No-21, Office No-4, M-13',
      addressLocality: 'Mussafah',
      addressRegion: 'Abu Dhabi',
      addressCountry: 'AE',
    },
    email: 'info@bigbirdtravel.com',
    openingHours: 'Mo-Sa 09:00-18:00',
  },
  areaServed: { '@type': 'Country', name: 'United Arab Emirates' },
  serviceType: 'UAE Family Visa Sponsorship',
  termsOfService: 'https://bigbirdtravel.com/terms',
};

function FamilyVisaExtraSections() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICE_SCHEMA) }} />

      <section className="py-10 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-4">UAE Family Visa — At a Glance</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {SUMMARY_CARDS.map(({ label, value, sub, Icon }) => (
              <div key={label} className="rounded-2xl border border-[#C3DCF5] bg-white p-4 shadow-sm flex flex-col gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={16} weight="fill" className="text-[#0068B3]" />
                </span>
                <p className="text-[10px] font-black uppercase tracking-wider text-[#0068B3]">{label}</p>
                <p className="font-display font-black text-[#071126] text-[14px] leading-tight">{value}</p>
                <p className="text-[11px] text-gray-400 leading-tight">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-6 lg:px-8 bg-white border-b border-gray-50">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 items-start">
          <div className="shrink-0 lg:w-60">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">About This Guide</span>
            <h2 className="font-display font-black text-[#071126] text-[20px] leading-tight">Who this is for</h2>
            <p className="text-gray-500 text-[13px] mt-2 leading-relaxed">
              For UAE residents in Abu Dhabi and Dubai who want their spouse, children or parents to live with them.
            </p>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GUIDE_FOR.map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-3 rounded-xl border border-[#D8EAF7] bg-[#F5FAFF] px-4 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={14} weight="fill" className="text-[#0068B3]" />
                </span>
                <span className="text-[13px] font-semibold text-[#071126]">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-4">
        <AIAnswerBox
          question="What is the minimum salary to sponsor family in the UAE?"
          answer="UAE authorities set a minimum monthly salary — commonly cited around AED 4,000, or AED 3,000 plus company-provided accommodation, though this can vary by emirate and case — to sponsor a spouse and children. Golden Visa holders are generally exempt from this requirement. Big Bird Travel, based in Mussafah, Abu Dhabi, checks a sponsor's exact salary and accommodation against current rules before starting the application."
        />
        <AIAnswerBox
          question="What documents are needed to sponsor a spouse or children in the UAE?"
          answer="Sponsors need their Emirates ID, UAE residence visa, salary certificate (or trade licence if self-employed), a valid Ejari tenancy contract, and — for the family member — an attested marriage certificate (spouse) or attested birth certificate (children), passport copies, and photographs. A medical fitness test and Emirates ID application follow once the family member is in the UAE."
        />
      </div>

      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Sponsorship by Relationship</span>
          <h2 className="font-display font-black text-[#071126] mb-2" style={{ fontSize: 'clamp(1.3rem, 2vw, 1.65rem)' }}>
            UAE family sponsorship requirements — 2026
          </h2>
          <p className="text-gray-500 text-[13px] mb-6 max-w-2xl">
            Thresholds are set by UAE authorities and can vary by emirate and case — confirm your exact eligibility with us.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-[#D8EAF7] shadow-sm">
            <table className="w-full text-left text-[13px] min-w-[600px]">
              <thead>
                <tr className="bg-[#071126] text-white">
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tl-2xl">Relationship</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider">Minimum Salary</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tr-2xl">Key Extra Document</th>
                </tr>
              </thead>
              <tbody>
                {RELATIONSHIP_TABLE.map((row, i) => (
                  <tr key={row.relation} className={`border-t border-[#E8F3FC] ${i % 2 === 0 ? 'bg-white' : 'bg-[#F5FAFF]'}`}>
                    <td className="px-5 py-3.5 font-semibold text-[#071126]">{row.relation}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide whitespace-nowrap ${
                        row.badge === 'green' ? 'bg-green-100 text-green-800' :
                        row.badge === 'blue'  ? 'bg-[#EAF7FF] text-[#0068B3]' :
                        row.badge === 'navy'  ? 'bg-[#071126] text-white' :
                                                'bg-gray-100 text-gray-600'
                      }`}>{row.minSalary}</span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-600">{row.extra}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-14 px-6 lg:px-8 bg-amber-50 border-y border-amber-100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-700 mb-3 block">⚠ Know Before You Apply</span>
            <h2 className="font-display font-black text-[#071126] mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              What actually slows family visa applications down
            </h2>
            <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
              It's rarely the salary check — it's unattested certificates and tenancy contracts not registered correctly. Both can be sorted in advance if you start early.
            </p>
            <div className="space-y-2.5 mt-6">
              <ExpandableTip title="Get your Ejari tenancy contract sorted first">
                A valid, registered tenancy contract in your own name is a core requirement. If you haven't registered your Ejari yet, do this before starting the visa process.
              </ExpandableTip>
              <ExpandableTip title="Start certificate attestation early">
                Marriage and birth certificates need attestation in the issuing country, then by the UAE Ministry of Foreign Affairs. This can take longer than every other step combined — start it as soon as you decide to sponsor.
              </ExpandableTip>
              <ExpandableTip title="Check the parent-sponsorship threshold separately">
                Sponsoring parents has its own, generally higher income requirement and additional conditions compared to spouse/child sponsorship. Don't assume the same salary that qualifies you for a spouse also qualifies you for a parent.
              </ExpandableTip>
              <ExpandableTip title="Book the medical test as soon as your family arrives">
                Once your family member is in the UAE on the entry permit, the medical fitness test and Emirates ID application need to happen promptly to avoid delays in the residence visa stamping.
              </ExpandableTip>
            </div>
          </div>

          <div className="bg-white border border-amber-200 rounded-2xl p-6 shadow-sm lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={18} weight="fill" className="text-[#0068B3]" />
              <span className="font-black text-[12px] uppercase tracking-widest text-[#0068B3]">Quick Eligibility Check</span>
            </div>
            <ul className="space-y-3 mb-5">
              {[
                { text: 'Salary AED 4,000+ or 3,000 + housing', result: 'Sponsor spouse & children', type: 'ok' },
                { text: 'Golden Visa holder', result: 'Sponsor without salary rule', type: 'ok' },
                { text: 'Want to sponsor parents', result: 'Higher threshold — check with us', type: 'warn' },
                { text: 'No Ejari tenancy contract yet', result: 'Register this first', type: 'warn' },
              ].map(item => (
                <li key={item.text} className="flex items-start gap-2.5">
                  {item.type === 'ok' && <CheckCircle size={14} weight="fill" className="text-green-500 shrink-0 mt-0.5" />}
                  {item.type === 'warn' && <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />}
                  <span className="text-[12px] text-gray-700"><strong>{item.text}</strong> → {item.result}</span>
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full rounded-xl bg-[#0F7A35] text-white text-[13px] font-black py-3.5 hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Check My Eligibility — Free
            </a>
            <p className="text-center text-[11px] text-gray-400 mt-2.5 leading-snug">
              Send your salary & relationship — honest answer, same day.
            </p>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#071126] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.10),transparent_55%)] pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#C9A227] mb-3 block">Authority Section</span>
          <h2 className="font-display font-black text-white mb-3" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
            What most family visa pages don't tell you
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIDDEN_FACTS.map(({ Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-[#0096FF]/20 bg-white/[0.045] p-5 hover:border-[#0096FF]/40 hover:bg-white/[0.06] transition-all">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0096FF]/10 border border-[#0096FF]/20 mb-4">
                  <Icon size={17} weight="fill" className="text-[#0096FF]" />
                </span>
                <h3 className="font-display font-black text-white text-[13px] mb-2 leading-tight">{title}</h3>
                <p className="text-blue-50/60 text-[12px] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-[#0096FF]/25 bg-white/[0.04] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="font-display font-black text-white text-[15px] mb-1">Ready to bring your family to the UAE?</p>
              <p className="text-blue-50/60 text-[13px]">Send your salary, relationship and tenancy details. We'll confirm eligibility and an honest cost — usually within the hour.</p>
            </div>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-5 py-3 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#041024] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.14),transparent_50%)]" />
        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0096FF] mb-3 block">Why Big Bird</span>
            <h2 className="font-display font-black text-white mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Family sponsorship, handled step by step
            </h2>
            <p className="text-blue-50/85 text-[15px] italic leading-relaxed mb-5 border-l-2 border-[#0096FF] pl-4">
              "Send your salary and relationship details. We'll confirm eligibility, guide document attestation, and manage the entry permit through to Emirates ID."
            </p>
            <p className="text-blue-50/70 text-[14px] leading-relaxed mb-4">
              We're a licensed travel agency in <strong className="text-white">Mussafah, Abu Dhabi</strong>. For family visas we:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                'Check your salary and accommodation against current thresholds.',
                'Guide certificate attestation for marriage and birth documents.',
                'Prepare and submit the entry permit application.',
                'Schedule the medical test and Emirates ID once family arrives.',
                'Track renewal dates alongside your own residence visa.',
              ].map(item => (
                <li key={item} className="flex items-start gap-3 text-[13px] text-blue-50/80">
                  <CheckCircle size={14} weight="fill" className="text-[#0096FF] shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-6 py-3.5 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={19} weight="fill" /> WhatsApp Us — Same-Day Response
            </a>
          </div>
          <div className="space-y-4">
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-3xl p-6">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/70 mb-4">Big Bird Travel — Agency Details</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { label: 'Office',   value: 'Mussafah 13, Abu Dhabi' },
                  { label: 'WhatsApp', value: '+971 56 178 9642' },
                  { label: 'License',  value: 'ADRA CN-5285999' },
                  { label: 'Response', value: 'Same day' },
                  { label: 'Hours',    value: 'Mon–Sat 9am–6pm UAE' },
                  { label: 'Serves',   value: 'Abu Dhabi · Dubai · UAE' },
                ].map(item => (
                  <div key={item.label}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/60 mb-0.5">{item.label}</p>
                    <p className="text-[13px] font-semibold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-2xl p-5">
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (<Star key={i} size={12} weight="fill" className="text-[#C9A227]" />))}
                <span className="text-[11px] text-blue-50/50 ml-2">Verified customer</span>
              </div>
              <blockquote className="text-[13px] text-blue-50/80 italic leading-relaxed">
                "I didn't know my parents' certificates needed attestation. They caught it early and my wife and kids' visas went through without a hitch."
              </blockquote>
              <p className="text-[11px] text-blue-50/40 mt-2">— UAE resident, Abu Dhabi</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Related Visa Services</span>
          <h2 className="font-display font-black text-[#071126] mb-6 text-[20px]">
            Family sponsored. Here's what else to plan.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {FAMILY_SERVICES.map(({ label, to, desc }) => (
              <Link key={label} to={to} className="group rounded-2xl border border-[#D8EAF7] bg-[#F5FAFF] p-4 hover:border-[#0096FF]/50 hover:bg-[#EAF7FF] hover:-translate-y-0.5 transition-all flex flex-col gap-2">
                <ArrowRight size={15} weight="bold" className="text-[#0068B3] group-hover:translate-x-0.5 transition-transform" />
                <p className="font-display font-black text-[#071126] text-[13px] leading-tight">{label}</p>
                <p className="text-[11px] text-gray-400">{desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-7 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><CalendarBlank size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Last Updated:</strong> September 2026</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><SealCheck size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Reviewed by:</strong> Big Bird Travel Visa Team</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><Shield size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">License:</strong> ADRA CN-5285999</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><MapPin size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Office:</strong> Mussafah 13, Abu Dhabi, UAE</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><WhatsappLogo size={13} weight="fill" className="text-green-600" /><span><strong className="text-[#071126]">WhatsApp:</strong> Same-day response</span></div>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="shrink-0">
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">Local Service</span>
              <h2 className="font-display font-black text-[#071126] text-[18px]">Areas We Serve</h2>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-[14px] mb-4">
                We assist family visa sponsors across <strong className="text-[#071126]">Abu Dhabi, Dubai, and the Northern Emirates</strong> — from our Mussafah office, with real one-on-one guidance.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Mussafah', 'Mohammed Bin Zayed City', 'Khalifa City', 'Al Nahyan', 'Abu Dhabi City', 'Al Ain', 'Dubai', 'Sharjah', 'Northern Emirates'].map(area => (
                  <span key={area} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EAF7FF] border border-[#C3DCF5] text-[12px] font-black text-[#0068B3]">
                    <MapPin size={11} weight="fill" />{area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function FamilyVisa() {
  return (
    <VisaDetailLayout
      visa={VISA_BY_ID['family-visa']}
      extraSections={<FamilyVisaExtraSections />}
    />
  );
}
